import { BooleanFilterValue, FILTER_BOOLEAN_EMPTY } from './constants';
import { DateRange, UnitsConsumidorasFilters } from './types';

const mapBooleanFilter = (value: BooleanFilterValue) => ({
  value: value === true || value === false ? value : undefined,
  isNull: value === FILTER_BOOLEAN_EMPTY ? true : undefined,
});

const booleanFilterFields = (filters: UnitsConsumidorasFilters) => {
  const acl = mapBooleanFilter(filters.aclBefore2025Law);
  const conn = mapBooleanFilter(filters.isNewConnection);
  const gen = mapBooleanFilter(filters.powerGenerator);

  return {
    aclBefore2025Law: acl.value,
    aclBefore2025LawIsNull: acl.isNull,
    isNewConnection: conn.value,
    isNewConnectionIsNull: conn.isNull,
    powerGenerator: gen.value,
    powerGeneratorIsNull: gen.isNull,
  };
};

const rangeFields = (filters: UnitsConsumidorasFilters) => ({
  volumeMwmMin: filters.volumeMwmRange?.min ?? undefined,
  volumeMwmMax: filters.volumeMwmRange?.max ?? undefined,
  exposurePercentageMin: filters.exposurePercentageRange?.min ?? undefined,
  exposurePercentageMax: filters.exposurePercentageRange?.max ?? undefined,
  managementCostMin: filters.managementCostRange?.min ?? undefined,
  managementCostMax: filters.managementCostRange?.max ?? undefined,
  demandPeakMin: filters.demandPeakRange?.min ?? undefined,
  demandPeakMax: filters.demandPeakRange?.max ?? undefined,
  demandOffPeakMin: filters.demandOffPeakRange?.min ?? undefined,
  demandOffPeakMax: filters.demandOffPeakRange?.max ?? undefined,
  demandPeakBeforeMin: filters.demandPeakBeforeRange?.min ?? undefined,
  demandPeakBeforeMax: filters.demandPeakBeforeRange?.max ?? undefined,
  demandOffPeakBeforeMin: filters.demandOffPeakBeforeRange?.min ?? undefined,
  demandOffPeakBeforeMax: filters.demandOffPeakBeforeRange?.max ?? undefined,
  demandPeakBeforeLawMin: filters.demandPeakBeforeLawRange?.min ?? undefined,
  demandPeakBeforeLawMax: filters.demandPeakBeforeLawRange?.max ?? undefined,
  demandOffPeakBeforeLawMin: filters.demandOffPeakBeforeLawRange?.min ?? undefined,
  demandOffPeakBeforeLawMax: filters.demandOffPeakBeforeLawRange?.max ?? undefined,
  generatorCostMin: filters.generatorCostRange?.min ?? undefined,
  generatorCostMax: filters.generatorCostRange?.max ?? undefined,
  migrationDateFrom: (filters.migrationDateRange as DateRange | null)?.from ?? undefined,
  migrationDateTo: (filters.migrationDateRange as DateRange | null)?.to ?? undefined,
  expectedMigrationDateFrom: (filters.expectedMigrationDateRange as DateRange | null)?.from ?? undefined,
  expectedMigrationDateTo: (filters.expectedMigrationDateRange as DateRange | null)?.to ?? undefined,
  clientSinceFrom: (filters.clientSinceRange as DateRange | null)?.from ?? undefined,
  clientSinceTo: (filters.clientSinceRange as DateRange | null)?.to ?? undefined,
  churnDateFrom: (filters.churnDateRange as DateRange | null)?.from ?? undefined,
  churnDateTo: (filters.churnDateRange as DateRange | null)?.to ?? undefined,
  createdAtFrom: (filters.createdAtRange as DateRange | null)?.from ?? undefined,
  createdAtTo: (filters.createdAtRange as DateRange | null)?.to ?? undefined,
  updatedAtFrom: (filters.updatedAtRange as DateRange | null)?.from ?? undefined,
  updatedAtTo: (filters.updatedAtRange as DateRange | null)?.to ?? undefined,
});

export const buildUnitsReportFilterFields = (filters: UnitsConsumidorasFilters) => ({
  term: filters.term || undefined,
  nameFilter: filters.nameFilter || undefined,
  legalNameFilter: filters.legalNameFilter || undefined,
  accountManagerIdFilter: filters.accountManagerIdFilter || undefined,
  crmIdFilter: filters.crmIdFilter || undefined,
  unitIdFilter: filters.unitIdFilter || undefined,
  companyIdFilter: filters.companyIdFilter || undefined,
  docNumberFilter: filters.docNumberFilter || undefined,
  cityFilter: filters.cityFilter || undefined,
  addressFilter: filters.addressFilter || undefined,
  postalCodeFilter: filters.postalCodeFilter || undefined,
  installationCodeFilter: filters.installationCodeFilter || undefined,
  newInstallationCodeFilter: filters.newInstallationCodeFilter || undefined,
  edcCustomerCodeFilter: filters.edcCustomerCodeFilter || undefined,
  assetCodeFilter: filters.assetCodeFilter || undefined,
  cceeProfileFilter: filters.cceeProfileFilter || undefined,
  status: filters.status ?? undefined,
  submarket: filters.submarket ?? undefined,
  productType: filters.productType ?? undefined,
  energyType: filters.energyType ?? undefined,
  unitType: filters.unitType ?? undefined,
  tariffSubgroup: filters.tariffSubgroup ?? undefined,
  tariffModality: filters.tariffModality ?? undefined,
  energyContractingModel: filters.energyContractingModel ?? undefined,
  docType: filters.docType ?? undefined,
  edc: filters.edc ?? undefined,
  exposure: filters.exposure ?? undefined,
  state: filters.state || undefined,
  ...booleanFilterFields(filters),
  ...rangeFields(filters),
});

export const buildUnitsReportGraphqlVariables = (
  page: number,
  limit: number,
  filters: UnitsConsumidorasFilters,
  sortBy: string | null,
  sortOrder: 'asc' | 'desc',
  nullKpi: string | null = null,
) => ({
  page,
  limit,
  ...buildUnitsReportFilterFields(filters),
  sortBy: sortBy ?? undefined,
  sortOrder: sortBy ? sortOrder : undefined,
  nullKpi: nullKpi ?? undefined,
});

const EXPORT_PARAM_KEYS: Record<string, string> = {
  term: 'term',
  nameFilter: 'name_filter',
  legalNameFilter: 'legal_name_filter',
  accountManagerIdFilter: 'account_manager_id_filter',
  crmIdFilter: 'crm_id_filter',
  unitIdFilter: 'unit_id_filter',
  companyIdFilter: 'company_id_filter',
  docNumberFilter: 'doc_number_filter',
  cityFilter: 'city_filter',
  addressFilter: 'address_filter',
  postalCodeFilter: 'postal_code_filter',
  installationCodeFilter: 'installation_code_filter',
  newInstallationCodeFilter: 'new_installation_code_filter',
  edcCustomerCodeFilter: 'edc_customer_code_filter',
  assetCodeFilter: 'asset_code_filter',
  cceeProfileFilter: 'ccee_profile_filter',
  status: 'status',
  submarket: 'submarket',
  productType: 'product_type',
  energyType: 'energy_type',
  unitType: 'unit_type',
  tariffSubgroup: 'tariff_subgroup',
  tariffModality: 'tariff_modality',
  energyContractingModel: 'energy_contracting_model',
  docType: 'doc_type',
  edc: 'edc',
  exposure: 'exposure',
  state: 'state',
  volumeMwmMin: 'volume_mwm_min',
  volumeMwmMax: 'volume_mwm_max',
  exposurePercentageMin: 'exposure_percentage_min',
  exposurePercentageMax: 'exposure_percentage_max',
  managementCostMin: 'management_cost_min',
  managementCostMax: 'management_cost_max',
  demandPeakMin: 'demand_peak_min',
  demandPeakMax: 'demand_peak_max',
  demandOffPeakMin: 'demand_off_peak_min',
  demandOffPeakMax: 'demand_off_peak_max',
  demandPeakBeforeMin: 'demand_peak_before_min',
  demandPeakBeforeMax: 'demand_peak_before_max',
  demandOffPeakBeforeMin: 'demand_off_peak_before_min',
  demandOffPeakBeforeMax: 'demand_off_peak_before_max',
  demandPeakBeforeLawMin: 'demand_peak_before_law_min',
  demandPeakBeforeLawMax: 'demand_peak_before_law_max',
  demandOffPeakBeforeLawMin: 'demand_off_peak_before_law_min',
  demandOffPeakBeforeLawMax: 'demand_off_peak_before_law_max',
  generatorCostMin: 'generator_cost_min',
  generatorCostMax: 'generator_cost_max',
  migrationDateFrom: 'migration_date_from',
  migrationDateTo: 'migration_date_to',
  expectedMigrationDateFrom: 'expected_migration_date_from',
  expectedMigrationDateTo: 'expected_migration_date_to',
  clientSinceFrom: 'client_since_from',
  clientSinceTo: 'client_since_to',
  churnDateFrom: 'churn_date_from',
  churnDateTo: 'churn_date_to',
  createdAtFrom: 'created_at_from',
  createdAtTo: 'created_at_to',
  updatedAtFrom: 'updated_at_from',
  updatedAtTo: 'updated_at_to',
};

const EXPORT_BOOLEAN_KEYS: Record<string, string> = {
  aclBefore2025Law: 'acl_before_2025_law',
  isNewConnection: 'is_new_connection',
  powerGenerator: 'power_generator',
};

export const appendUnitsReportExportParams = (
  params: URLSearchParams,
  filters: UnitsConsumidorasFilters,
  nullKpi: string | null = null,
) => {
  const fields = buildUnitsReportFilterFields(filters);

  Object.entries(EXPORT_PARAM_KEYS).forEach(([sourceKey, targetKey]) => {
    const value = fields[sourceKey as keyof typeof fields];
    if (value != null && value !== '') {
      params.append(targetKey, String(value));
    }
  });

  if (nullKpi) {
    params.append('null_kpi', nullKpi);
  }

  Object.entries(EXPORT_BOOLEAN_KEYS).forEach(([sourceKey, targetKey]) => {
    const value = filters[sourceKey as keyof UnitsConsumidorasFilters] as BooleanFilterValue;
    if (value === FILTER_BOOLEAN_EMPTY) {
      params.append(`${targetKey}_is_null`, 'true');
      return;
    }
    if (value === true || value === false) {
      params.append(targetKey, String(value));
    }
  });
};
